"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { ArrowRightIcon } from "lucide-react";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

const quickActionButtonVariants = cva(
  "group inline-flex items-center gap-1.5 rounded-lg border font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "border-border bg-muted/50 hover:border-primary/30 hover:bg-primary/5 active:scale-[0.98]",
        subtle:
          "border-transparent bg-transparent hover:border-border hover:bg-muted/50 active:scale-[0.98]",
        inline:
          "border-muted-foreground/30 border-dashed bg-transparent text-muted-foreground hover:border-primary/50 hover:text-foreground",
        ghost: "border-transparent hover:bg-muted/80 active:scale-[0.98]",
      },
      size: {
        sm: "px-2 py-1 text-xs",
        md: "px-3 py-1.5 text-sm",
        lg: "px-4 py-2 text-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
    },
  }
);

export type QuickActionButtonProps =
  React.ButtonHTMLAttributes<HTMLButtonElement> &
    VariantProps<typeof quickActionButtonVariants> & {
      /** Display label for the button */
      label: string;
      /** Optional icon to display before the label */
      icon?: ReactNode;
      /** Whether to show the arrow indicator */
      showArrow?: boolean;
    };

const QuickActionButton = forwardRef<HTMLButtonElement, QuickActionButtonProps>(
  (
    { className, variant, size, label, icon, showArrow = true, ...props },
    ref
  ) => {
    return (
      <button
        className={cn(quickActionButtonVariants({ variant, size, className }))}
        ref={ref}
        type="button"
        {...props}
      >
        {icon && (
          <span className="text-muted-foreground transition-colors group-hover:text-foreground">
            {icon}
          </span>
        )}
        <span>{label}</span>
        {showArrow && (
          <ArrowRightIcon className="size-3 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100" />
        )}
      </button>
    );
  }
);

QuickActionButton.displayName = "QuickActionButton";

export { QuickActionButton, quickActionButtonVariants };
